(() => {
  if (customElements.get('media-gallery')) return;

  class AbokProductMediaGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.mainElement = this.querySelector('[data-gallery-main]');
      this.thumbsElement = this.querySelector('[data-gallery-thumbs]');
      this.statusElement = this.querySelector('[data-gallery-status]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.abortController = new AbortController();

      if (!this.mainElement) return;
      if (typeof window.Swiper !== 'function') {
        this.swiperRetryCount = (this.swiperRetryCount || 0) + 1;
        if (this.swiperRetryCount <= 40) {
          this.swiperRetryTimer = window.setTimeout(() => this.connectedCallback(), 50);
        }
        return;
      }

      this.initialized = true;
      this.initializeSwipers();
      this.bindThumbnailControls();
      this.subscribeToVariantChanges();

      const initialMediaId = this.pendingMediaId || this.dataset.initialMediaId;
      if (initialMediaId) this.setActiveMedia(initialMediaId, false, false);
      else this.syncActiveState(0, false);
    }

    disconnectedCallback() {
      window.clearTimeout(this.swiperRetryTimer);
      this.abortController?.abort();
      this.variantChangeUnsubscriber?.();
      this.mainSwiper?.destroy(true, true);
      this.thumbsSwiper?.destroy(true, true);
      this.mainSwiper = null;
      this.thumbsSwiper = null;
      this.initialized = false;
    }

    initializeSwipers() {
      if (this.thumbsElement) {
        this.thumbsSwiper = new window.Swiper(this.thumbsElement, {
          slidesPerView: 'auto',
          spaceBetween: 12,
          freeMode: true,
          grabCursor: true,
          watchSlidesProgress: true,
          watchOverflow: true,
          observer: true,
          observeParents: true,
          resizeObserver: true,
          a11y: false,
          on: {
            init: (swiper) => this.setThumbsLocked(swiper.isLocked),
            lock: () => this.setThumbsLocked(true),
            unlock: () => this.setThumbsLocked(false),
          },
        });
      }

      this.mainSwiper = new window.Swiper(this.mainElement, {
        slidesPerView: 1,
        speed: this.reducedMotion.matches ? 0 : 360,
        rewind: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        resizeObserver: true,
        preventClicks: true,
        preventClicksPropagation: true,
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        navigation: {
          nextEl: this.querySelector('[data-gallery-next]'),
          prevEl: this.querySelector('[data-gallery-previous]'),
        },
        thumbs: this.thumbsSwiper ? { swiper: this.thumbsSwiper } : undefined,
        a11y: {
          enabled: true,
          containerMessage: this.getAttribute('aria-label') || 'Product media gallery',
          containerRoleDescriptionMessage: 'carousel',
          itemRoleDescriptionMessage: 'slide',
          slideLabelMessage: '{{index}} / {{slidesLength}}',
        },
        on: {
          init: (swiper) => this.syncActiveState(swiper.activeIndex, false),
          slideChange: (swiper) => this.syncActiveState(swiper.activeIndex, true),
        },
      });
    }

    bindThumbnailControls() {
      this.querySelectorAll('[data-gallery-thumbnail]').forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            const slide = button.closest('[data-media-id]');
            if (slide) this.setActiveMedia(slide.dataset.mediaId, false, true);
          },
          { signal: this.abortController.signal },
        );
      });
    }

    subscribeToVariantChanges() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (`${data?.sectionId}` !== `${this.dataset.sectionId}` || !data?.variant?.featured_media?.id) return;
        this.setActiveMedia(`${this.dataset.sectionId}-${data.variant.featured_media.id}`, false, false);
      });
    }

    setActiveMedia(mediaId, _prepend = false, announce = true) {
      this.pendingMediaId = mediaId;
      if (!this.mainSwiper) return;

      this.mainSwiper.update();
      this.thumbsSwiper?.update();

      const index = Array.from(this.mainSwiper.slides).findIndex((slide) => slide.dataset.mediaId === mediaId);
      if (index < 0) return;

      if (index === this.mainSwiper.activeIndex) {
        this.syncActiveState(index, announce);
      } else {
        this.pendingAnnouncement = announce;
        this.mainSwiper.slideTo(index, this.reducedMotion.matches ? 0 : undefined);
      }
    }

    syncActiveState(index, announce) {
      const activeSlide = this.mainSwiper?.slides?.[index];
      if (!activeSlide) return;

      const mediaId = activeSlide.dataset.mediaId;
      this.querySelectorAll('[data-gallery-thumbnail]').forEach((button) => {
        const isActive = button.closest('[data-media-id]')?.dataset.mediaId === mediaId;
        if (isActive) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });

      const activeThumb = this.querySelector(`.product-gallery-thumbs__slide[data-media-id="${CSS.escape(mediaId)}"]`);
      if (activeThumb && this.thumbsSwiper) {
        const thumbIndex = Array.from(this.thumbsSwiper.slides).indexOf(activeThumb);
        if (thumbIndex >= 0) this.thumbsSwiper.slideTo(thumbIndex, this.reducedMotion.matches ? 0 : 220);
      }

      if (announce || this.pendingAnnouncement) this.announceMedia(index + 1);
      this.pendingAnnouncement = false;
    }

    announceMedia(index) {
      if (!this.statusElement) return;

      const fallback = `Media ${index} of ${this.mainSwiper.slides.length}`;
      const template = window.accessibilityStrings?.imageAvailable;
      this.statusElement.textContent = template ? template.replace('[index]', index) : fallback;
    }

    setThumbsLocked(locked) {
      this.dataset.thumbsLocked = String(locked);
    }
  }

  customElements.define('media-gallery', AbokProductMediaGallery);
})();
